import type { FC } from 'react'
import type { ProjectSummary } from '@/types'

import {
    ArrowLeft,
    ArrowRight,
    ArrowUpRight,
    TrendingUp
} from 'lucide-react'
import { toPlatform } from '@/lib/platforms'
import { useDragScroll } from '@/hooks/use-drag-scroll'
import { Button } from '@/components/kit/button'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { PlatformIcon } from '@/components/kit/platform-icon'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { Tilt } from '@/components/kit/tilt'

interface CaseCarouselProps {
    projects: Array<ProjectSummary>
}

export const CaseCard: FC<{ project: ProjectSummary, sizes: string }> = ({ project, sizes }) => {
    return (
        <Tilt max={5}>
            <article className='relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-sand sm:aspect-[16/11] lg:aspect-[16/10]'>
                {project.cover && (
                    <Photo
                        src={project.cover}
                        alt={project.cover_alt ?? ''}
                        sizes={sizes}
                        grain
                        className='absolute inset-0'
                        imgClassName='transition-transform duration-[1.4s] ease-expo group-hover:scale-105'
                    />
                )}

                <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/25' />

                <div className='absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-4 p-5 md:p-7'>
                    <span
                        className='rounded-full bg-cream/90 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-ink backdrop-blur'
                        data-mdlfy-cms={`projects:${project.id}:industry`}
                    >
                        {project.industry}
                    </span>

                    <span className='flex gap-1.5'>
                        {project.platforms.map(label => {
                            const platform = toPlatform(label)

                            return platform ? (
                                <span
                                    key={label}
                                    title={label}
                                    className='flex size-8 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-md'
                                >
                                    <PlatformIcon
                                        name={platform}
                                        className='size-3.5'
                                    />
                                    <span className='sr-only'>{label}</span>
                                </span>
                            ) : null
                        })}
                    </span>
                </div>

                <div className='absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-6 p-5 text-white md:p-8'>
                    <div className='min-w-0'>
                        <p
                            className='font-heading text-[clamp(2.25rem,5vw,4.5rem)] font-extrabold leading-[0.9] tracking-[-0.04em]'
                            data-mdlfy-cms={`projects:${project.id}:brand`}
                        >
                            {project.brand}
                        </p>

                        {project.headline_result && (
                            <p className='mt-4 inline-flex items-center gap-2 rounded-full bg-ember py-1.5 pl-2.5 pr-4 text-sm font-semibold text-ink'>
                                <TrendingUp className='size-4' />
                                <span data-mdlfy-cms={`projects:${project.id}:headline_result`}>{project.headline_result}</span>
                            </p>
                        )}
                    </div>

                    <span className='hidden size-14 shrink-0 items-center justify-center rounded-full bg-cream text-ink transition-transform duration-500 ease-expo group-hover:rotate-45 sm:flex'>
                        <ArrowUpRight className='size-5' />
                    </span>
                </div>
            </article>
        </Tilt>
    )
}

export const CaseCarousel: FC<CaseCarouselProps> = ({ projects }) => {
    const scrollerRef = useDragScroll<HTMLDivElement>()

    if (!projects.length) return null

    const scroll = (direction: 1 | -1) => {
        const scroller = scrollerRef.current
        const card = scroller?.querySelector<HTMLElement>('[data-slide]')

        if (!scroller || !card) return

        scroller.scrollBy({ left: direction * (card.offsetWidth + 20), behavior: 'smooth' })
    }

    return (
        <section
            id='case-studies'
            aria-labelledby='case-studies-title'
            className='overflow-hidden py-20 md:py-28'
        >
            <div className='mx-auto flex max-w-[1400px] flex-col gap-8 px-5 md:flex-row md:items-end md:justify-between md:px-10'>
                <div>
                    <SectionLabel index='12'>Case notes</SectionLabel>
                    <SplitText
                        as='h2'
                        id='case-studies-title'
                        lines={['Brands that', 'caught the lift']}
                        italic='lift'
                        className='mt-5 text-[clamp(2.75rem,6.4vw,6rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                    />
                </div>

                <Reveal
                    delay={200}
                    className='flex items-center gap-3'
                >
                    <button
                        type='button'
                        onClick={() => scroll(-1)}
                        aria-label='Previous case study'
                        className='flex size-12 items-center justify-center rounded-full border border-ink/15 transition-colors hover:border-ink hover:bg-ink hover:text-cream'
                    >
                        <ArrowLeft className='size-5' />
                    </button>
                    <button
                        type='button'
                        onClick={() => scroll(1)}
                        aria-label='Next case study'
                        className='flex size-12 items-center justify-center rounded-full border border-ink/15 transition-colors hover:border-ink hover:bg-ink hover:text-cream'
                    >
                        <ArrowRight className='size-5' />
                    </button>
                    <Button
                        href='/work'
                        variant='dark'
                        className='ml-2'
                    >
                        All case notes
                    </Button>
                </Reveal>
            </div>

            <Reveal
                delay={150}
                className='mt-12'
            >
                <div
                    ref={scrollerRef}
                    className='no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 pb-6 pt-2 data-[dragging=true]:snap-none data-[dragging=true]:scroll-auto md:px-10 xl:px-[max(2.5rem,calc((100vw_-_1400px)/2_+_2.5rem))]'
                >
                    {projects.map(project => (
                        <PageLink
                            key={project.id}
                            href={`/work/${project.slug}`}
                            data-slide=''
                            data-cursor='Open'
                            draggable={false}
                            aria-label={`${project.brand} case study`}
                            className='group block w-[86vw] shrink-0 snap-start sm:w-[72vw] lg:w-[min(62vw,900px)]'
                        >
                            <CaseCard
                                project={project}
                                sizes='(min-width: 1024px) 900px, 86vw'
                            />
                        </PageLink>
                    ))}

                    <PageLink
                        href='/work'
                        data-cursor='Open'
                        draggable={false}
                        className='group flex w-[60vw] shrink-0 snap-start flex-col justify-between rounded-[2rem] border border-ink/10 p-7 transition-colors hover:bg-ink hover:text-cream sm:w-[40vw] lg:w-[min(28vw,380px)]'
                    >
                        <span className='font-mono text-[11px] uppercase tracking-[0.14em] text-stone group-hover:text-cream/60'>The archive</span>
                        <span className='font-heading text-4xl font-bold leading-[0.95] tracking-[-0.04em]'>
                            Six brands, six forecasts that came true
                        </span>
                        <span className='flex size-14 items-center justify-center rounded-full bg-ember text-ink transition-transform duration-500 ease-expo group-hover:rotate-45'>
                            <ArrowUpRight className='size-5' />
                        </span>
                    </PageLink>
                </div>
            </Reveal>
        </section>
    )
}
